/**
 * Server functions for feature flag evaluation.
 *
 * These are called from client components to get flag values.
 *
 * Aliases: feature flags, get flags, evaluate flags
 */

import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

const evaluateFlagsRequestSchema = z.object({
  keys: z.array(z.string()).min(1).max(50),
});

async function tryGetUserId(): Promise<string | undefined> {
  try {
    const { getAuthenticatedUser } = await import("~/lib/server/auth-helpers");
    const user = await getAuthenticatedUser();
    return user.id;
  } catch {
    return undefined;
  }
}

/**
 * Evaluate feature flags for the current user (authenticated or not).
 */
export const evaluateFeatureFlagsForUser = createServerFn({ method: "POST" })
  .validator(evaluateFlagsRequestSchema)
  .handler(async (ctx) => {
    const { keys } = ctx.data;
    const userId = await tryGetUserId();

    const { evaluateFeatureFlags } = await import("~/lib/server/feature-flags");
    const results = await evaluateFeatureFlags(keys, userId);

    return Object.fromEntries(
      Object.entries(results).map(([key, result]) => [key, result.enabled]),
    );
  });

/**
 * Evaluate a single feature flag for the current user (authenticated or not).
 */
export const evaluateFeatureFlagForUser = createServerFn({ method: "POST" })
  .validator(z.object({ key: z.string() }))
  .handler(async (ctx) => {
    const { key } = ctx.data;
    const userId = await tryGetUserId();

    const { evaluateFeatureFlag } = await import("~/lib/server/feature-flags");
    const result = await evaluateFeatureFlag(key, userId);

    return result.enabled;
  });
